import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  FormControl,
  TextField,
  Typography,
  Button,
  Divider,
  Box,
  IconButton,
  InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Colorize, Save } from "@material-ui/icons";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import ColorBoxModal from "../ColorBoxModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  colorField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  colorPreview: {
    width: 30,
    height: 30,
    borderRadius: 4,
    border: "1px solid #ddd",
  },
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: "1px solid #e0e0e0",
  },
  actionButtons: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
}));

const HomePageCustomization = ({ settings, companyId }) => {
  const classes = useStyles();
  const { update } = useSettings();
  
  // State for color customization
  const [primaryColor, setPrimaryColor] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("");
  const [textColor, setTextColor] = useState("");
  const [accentColor, setAccentColor] = useState("");
  
  // State for font customization
  const [fontFamily, setFontFamily] = useState("");
  const [fontSize, setFontSize] = useState("");
  
  // State for section content
  const [heroTitle, setHeroTitle] = useState("");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroBadge, setHeroBadge] = useState("");
  const [sectionFeaturesTitle, setSectionFeaturesTitle] = useState("");
  const [sectionFeaturesDescription, setSectionFeaturesDescription] = useState("");
  const [sectionPricingTitle, setSectionPricingTitle] = useState("");
  const [sectionPricingDescription, setSectionPricingDescription] = useState("");
  const [ctaTitle, setCtaTitle] = useState("");
  const [ctaDescription, setCtaDescription] = useState("");
  
  // Loading state
  const [loading, setLoading] = useState(true);
  
  // Color picker modals
  const [colorPickerOpen, setColorPickerOpen] = useState(null);
  
  // Load existing settings
  useEffect(() => {
    // Define default values
    const heroBadgeDefault = "Plataforma Completa de Atendimento";
    const heroTitleDefault = "Transforme seu Atendimento com a TicketWhats";
    const heroDescriptionDefault = "Gerencie WhatsApp, Facebook, Instagram e mais canais em uma única plataforma. Automatize processos, integre com n8n e escale seu atendimento.";
    const sectionFeaturesTitleDefault = "Recursos";
    const sectionFeaturesDescriptionDefault = "Tudo que você precisa para gerenciar seu atendimento em um só lugar";
    const sectionPricingTitleDefault = "Planos para";
    const sectionPricingDescriptionDefault = "Escolha o plano ideal para o seu negócio";
    const ctaTitleDefault = "Pronto para transformar seu atendimento?";
    const ctaDescriptionDefault = "Comece gratuitamente e veja como podemos ajudar seu negócio a crescer";
    
    if (Array.isArray(settings) && settings.length) {
      // Colors
      const primaryColorSetting = settings.find(s => s.key === "homePagePrimaryColor")?.value || "";
      const backgroundColorSetting = settings.find(s => s.key === "homePageBackgroundColor")?.value || "";
      const textColorSetting = settings.find(s => s.key === "homePageTextColor")?.value || "";
      const accentColorSetting = settings.find(s => s.key === "homePageAccentColor")?.value || "";
      
      // Fonts
      const fontFamilySetting = settings.find(s => s.key === "homePageFontFamily")?.value || "";
      const fontSizeSetting = settings.find(s => s.key === "homePageFontSize")?.value || "";
      
      // Content - Load default values if settings are empty
      const heroBadgeSetting = settings.find(s => s.key === "homePageHeroBadge")?.value || heroBadgeDefault;
      const heroTitleSetting = settings.find(s => s.key === "homePageHeroTitle")?.value || heroTitleDefault;
      const heroDescriptionSetting = settings.find(s => s.key === "homePageHeroDescription")?.value || heroDescriptionDefault;
      const sectionFeaturesTitleSetting = settings.find(s => s.key === "homePageFeaturesTitle")?.value || sectionFeaturesTitleDefault;
      const sectionFeaturesDescriptionSetting = settings.find(s => s.key === "homePageFeaturesDescription")?.value || sectionFeaturesDescriptionDefault;
      const sectionPricingTitleSetting = settings.find(s => s.key === "homePagePricingTitle")?.value || sectionPricingTitleDefault;
      const sectionPricingDescriptionSetting = settings.find(s => s.key === "homePagePricingDescription")?.value || sectionPricingDescriptionDefault;
      const ctaTitleSetting = settings.find(s => s.key === "homePageCtaTitle")?.value || ctaTitleDefault;
      const ctaDescriptionSetting = settings.find(s => s.key === "homePageCtaDescription")?.value || ctaDescriptionDefault;
      
      setPrimaryColor(primaryColorSetting);
      setBackgroundColor(backgroundColorSetting);
      setTextColor(textColorSetting);
      setAccentColor(accentColorSetting);
      setFontFamily(fontFamilySetting);
      setFontSize(fontSizeSetting);
      setHeroBadge(heroBadgeSetting);
      setHeroTitle(heroTitleSetting);
      setHeroDescription(heroDescriptionSetting);
      setSectionFeaturesTitle(sectionFeaturesTitleSetting);
      setSectionFeaturesDescription(sectionFeaturesDescriptionSetting);
      setSectionPricingTitle(sectionPricingTitleSetting);
      setSectionPricingDescription(sectionPricingDescriptionSetting);
      setCtaTitle(ctaTitleSetting);
      setCtaDescription(ctaDescriptionSetting);
    } else {
      // If no settings are available, load default values
      setHeroBadge(heroBadgeDefault);
      setHeroTitle(heroTitleDefault);
      setHeroDescription(heroDescriptionDefault);
      setSectionFeaturesTitle(sectionFeaturesTitleDefault);
      setSectionFeaturesDescription(sectionFeaturesDescriptionDefault);
      setSectionPricingTitle(sectionPricingTitleDefault);
      setSectionPricingDescription(sectionPricingDescriptionDefault);
      setCtaTitle(ctaTitleDefault);
      setCtaDescription(ctaDescriptionDefault);
    }
    
    setLoading(false);
  }, [settings]);
  
  const handleSaveSetting = async (key, value) => {
    try {
      await update({ key, value, companyId });
      toast.success(i18n.t("settings.components.homePageCustomization.toasts.saveSuccess"));
    } catch (err) {
      toast.error(i18n.t("settings.components.homePageCustomization.toasts.saveError"));
      console.error(err);
    }
  };
  
  const handleSaveAll = async () => {
    const settingsToUpdate = [
      { key: "homePagePrimaryColor", value: primaryColor },
      { key: "homePageBackgroundColor", value: backgroundColor },
      { key: "homePageTextColor", value: textColor },
      { key: "homePageAccentColor", value: accentColor },
      { key: "homePageFontFamily", value: fontFamily },
      { key: "homePageFontSize", value: fontSize },
      { key: "homePageHeroTitle", value: heroTitle },
      { key: "homePageHeroDescription", value: heroDescription },
      { key: "homePageHeroBadge", value: heroBadge },
      { key: "homePageFeaturesTitle", value: sectionFeaturesTitle },
      { key: "homePageFeaturesDescription", value: sectionFeaturesDescription },
      { key: "homePagePricingTitle", value: sectionPricingTitle },
      { key: "homePagePricingDescription", value: sectionPricingDescription },
      { key: "homePageCtaTitle", value: ctaTitle },
      { key: "homePageCtaDescription", value: ctaDescription },
    ];
    
    try {
      for (const setting of settingsToUpdate) {
        await update({ ...setting, companyId });
      }
      toast.success(i18n.t("settings.components.homePageCustomization.toasts.saveAllSuccess"));
    } catch (err) {
      toast.error(i18n.t("settings.components.homePageCustomization.toasts.saveSettingsError"));
      console.error(err);
    }
  };
  
  const openColorPicker = (colorType) => {
    setColorPickerOpen(colorType);
  };
  
  const closeColorPicker = () => {
    setColorPickerOpen(null);
  };
  
  const handleColorChange = (color, colorType) => {
    const hex = typeof color === "string"
      ? color
      : (color?.hex ? `#${color.hex.replace("#", "")}` : (color?.raw ? color.raw : ""));
    const hexColor = hex ? (hex.startsWith("#") ? hex : `#${hex}`) : "#000000";
    switch (colorType) {
      case "primary":
        setPrimaryColor(hexColor);
        break;
      case "background":
        setBackgroundColor(hexColor);
        break;
      case "text":
        setTextColor(hexColor);
        break;
      case "accent":
        setAccentColor(hexColor);
        break;
      default:
        break;
    }
    closeColorPicker();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <div>{i18n.t("settings.components.homePageCustomization.loading")}</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Color Customization */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("settings.components.homePageCustomization.colorCustomization")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.primaryColor")}
                variant="outlined"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => openColorPicker("primary")}
                      >
                        <Colorize />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={classes.colorField}>
                <div 
                  className={classes.colorPreview} 
                  style={{ backgroundColor: primaryColor }}
                />
                <span>{primaryColor}</span>
              </div>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.backgroundColor")}
                variant="outlined"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => openColorPicker("background")}
                      >
                        <Colorize />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={classes.colorField}>
                <div 
                  className={classes.colorPreview} 
                  style={{ backgroundColor: backgroundColor }}
                />
                <span>{backgroundColor}</span>
              </div>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.textColor")}
                variant="outlined"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => openColorPicker("text")}
                      >
                        <Colorize />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={classes.colorField}>
                <div 
                  className={classes.colorPreview} 
                  style={{ backgroundColor: textColor }}
                />
                <span>{textColor}</span>
              </div>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.accentColor")}
                variant="outlined"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => openColorPicker("accent")}
                      >
                        <Colorize />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div className={classes.colorField}>
                <div 
                  className={classes.colorPreview} 
                  style={{ backgroundColor: accentColor }}
                />
                <span>{accentColor}</span>
              </div>
            </FormControl>
          </Grid>
        </Grid>
      </div>
      
      <Divider />
      
      {/* Font Customization */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("settings.components.homePageCustomization.fontCustomization")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.fontFamily")}
                variant="outlined"
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                size="small"
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formField} fullWidth>
              <TextField
                label={i18n.t("settings.components.homePageCustomization.fontSize")}
                variant="outlined"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                size="small"
                type="number"
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
      
      <Divider />
      
      {/* Content Customization */}
      <div className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("settings.components.homePageCustomization.contentCustomization")}
        </Typography>
        
        {/* Hero Section */}
        <Paper className={classes.sectionCard}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            {i18n.t("settings.components.homePageCustomization.heroSection")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("settings.components.homePageCustomization.badgeText")}
                  variant="outlined"
                  value={heroBadge}
                  onChange={(e) => setHeroBadge(e.target.value)}
                  size="small"
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("settings.components.homePageCustomization.mainTitle")}
                  variant="outlined"
                  value={heroTitle}
                  onChange={(e) => setHeroTitle(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.description")}
                  variant="outlined"
                  value={heroDescription}
                  onChange={(e) => setHeroDescription(e.target.value)}
                  size="small"
                  multiline
                  rows={3}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Features Section */}
        <Paper className={classes.sectionCard}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            {i18n.t("settings.components.homePageCustomization.featuresSection")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.title")}
                  variant="outlined"
                  value={sectionFeaturesTitle}
                  onChange={(e) => setSectionFeaturesTitle(e.target.value)}
                  size="small"
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.description")}
                  variant="outlined"
                  value={sectionFeaturesDescription}
                  onChange={(e) => setSectionFeaturesDescription(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Pricing Section */}
        <Paper className={classes.sectionCard}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            {i18n.t("settings.components.homePageCustomization.pricingSection")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.title")}
                  variant="outlined"
                  value={sectionPricingTitle}
                  onChange={(e) => setSectionPricingTitle(e.target.value)}
                  size="small"
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.description")}
                  variant="outlined"
                  value={sectionPricingDescription}
                  onChange={(e) => setSectionPricingDescription(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        
        {/* CTA Section */}
        <Paper className={classes.sectionCard}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            {i18n.t("settings.components.homePageCustomization.ctaSection")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.title")}
                  variant="outlined"
                  value={ctaTitle}
                  onChange={(e) => setCtaTitle(e.target.value)}
                  size="small"
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl className={classes.formField} fullWidth>
                <TextField
                  label={i18n.t("common.description")}
                  variant="outlined"
                  value={ctaDescription}
                  onChange={(e) => setCtaDescription(e.target.value)}
                  size="small"
                  multiline
                  rows={2}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </div>
      
      {/* Action Buttons */}
      <div className={classes.actionButtons}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSaveAll}
        >
          {i18n.t("settings.components.homePageCustomization.saveAll")}
        </Button>
      </div>
      
      {/* Color Pickers */}
      <ColorBoxModal
        open={colorPickerOpen === "primary"}
        handleClose={closeColorPicker}
        onChange={(color) => handleColorChange(color, "primary")}
        currentColor={primaryColor}
      />
      
      <ColorBoxModal
        open={colorPickerOpen === "background"}
        handleClose={closeColorPicker}
        onChange={(color) => handleColorChange(color, "background")}
        currentColor={backgroundColor}
      />
      
      <ColorBoxModal
        open={colorPickerOpen === "text"}
        handleClose={closeColorPicker}
        onChange={(color) => handleColorChange(color, "text")}
        currentColor={textColor}
      />
      
      <ColorBoxModal
        open={colorPickerOpen === "accent"}
        handleClose={closeColorPicker}
        onChange={(color) => handleColorChange(color, "accent")}
        currentColor={accentColor}
      />
    </div>
  );
};

export default HomePageCustomization;
